import React, { useState } from 'react';
import { useTranslation } from '../../hooks/useTranslation';
import Checkbox from './Checkbox';


export default function Step3({ formData, setFormData }) {
  const { t } = useTranslation();
  const storyCount = (formData.story || '').length;
  const [errors, setErrors] = useState({});

  const isVideoRequired = () => {
    const methods = formData.videoAltMethods || [];
    return !(methods.includes('whatsapp') || methods.includes('email'));
  };

  const isValidUrl = (s) => {
    if (!s) return false;
    try {
      const u = new URL(s);
      return u.protocol === 'http:' || u.protocol === 'https:';
    } catch (_) {
      return false;
    }
  };

  const setField = (key) => (e) => {
    const val = e.target.value;
    setFormData((prev) => ({ ...prev, [key]: val }));
    if (key === 'story') {
      const len = (val || '').trim().length;
      setErrors((prev) => ({ ...prev, story: len === 0 ? t('validation.required') : (len < 150 ? t('validation.minLength10') : '') }));
    }
    if (key === 'videoUrl') {
      const required = isVideoRequired();
      const msg = required && !val
        ? t('validation.required')
        : (val && !isValidUrl(val) ? t('validation.invalidUrl') : '');
      setErrors((prev) => ({ ...prev, videoUrl: msg }));
    }
  };

  const setSingleChoice = (key, val) => {
    const nextVal = (formData[key] === val) ? '' : val;
    setFormData((prev) => ({ ...prev, [key]: nextVal }));
    setErrors((prev) => ({ ...prev, [key]: nextVal ? '' : t('validation.required') }));
  };

  const toggleList = (key, value) => {
    const list = new Set(formData[key] || []);
    if (list.has(value)) list.delete(value); else list.add(value);
    const next = Array.from(list);
    setFormData((prev) => ({ ...prev, [key]: next }));
    if (key === 'videoAltMethods') {
      const required = !(next.includes('whatsapp') || next.includes('email'));
      const hasVideo = Boolean((formData.videoUrl || '').trim());
      setErrors((prev) => ({ ...prev, videoUrl: required && !hasVideo ? t('validation.required') : '' }));
    }
  };

  return (
    <div className="space-y-5">
      <p className='text-[20px]  my-5 font-[700] text-[#5D4A9D] text-start'>{t('registration.step3Title')}</p>

      <div>
        <div className="flex items-center justify-between mb-1">
          <label className="block primary-text text-[16px]   font-[500]">{t('registration.performance.story.label')} <span>*</span></label>
          {/* <span className="text-[#7D7D7D] text-[12px]">{t('registration.hints.storyChars')}</span> */}
        </div>
        <textarea
          rows={5}
          value={formData.story || ''}
          onChange={setField('story')}
          onBlur={() => {
            const len = (formData.story || '').trim().length;
            setErrors((prev) => ({ ...prev, story: len === 0 ? t('validation.required') : (len < 150 ? t('validation.minLength10') : '') }));
          }}
          placeholder={t('registration.performance.story.placeholder')}
          className="w-full border-[1px] rounded-[8px] placeholder:text-[#858585] px-[18px] py-3 bg-[#F5F5F5] border-[#E6E6E6] placeholder:text-[16px] text-[16px] focus:bg-[#fff] focus:outline-none focus:ring-[1.5px] focus:ring-[#5D4A9D]"
        />
        {errors.story ? <p className="text-red-500 text-[12px] mt-1 text-start ">{errors.story}</p> : null}
        <div className="flex items-center justify-start text-[#7D7D7D] text-[12px]">{storyCount} - 250 characters</div>
      </div>

      <div>
        <label className="block primary-text text-[16px]   font-[500] mb-2 text-start mt-4">{t('registration.performance.performedBefore.label')} <span>*</span></label>
        <div className="space-y-3">
          {['yes', 'no'].map((v) => (
            <Checkbox
              key={v}
              label={t(`registration.performance.performedBefore.options.${v}`)}
              checked={(formData.performedBefore || '') === v}
              onChange={() => setSingleChoice('performedBefore', v)}
            />
          ))}
        {errors.performedBefore ? <p className="text-red-500 text-[12px] mt-1 text-start ">{errors.performedBefore}</p> : null}
        </div>
      </div>

      <div>
        <label className="block primary-text text-[16px]   font-[500] mb-2 text-start mt-4">{t('registration.performance.skillLevel.label')} <span>*</span></label>
        <div className="space-y-3">
          {['beginner', 'intermediate', 'professional'].map((v) => (
            <Checkbox
              key={v}
              label={t(`registration.performance.skillLevel.options.${v}`)}
              checked={(formData.skillLevel || '') === v}
              onChange={() => setSingleChoice('skillLevel', v)}
            />
          ))}
        {errors.skillLevel ? <p className="text-red-500 text-[12px] mt-1 text-start ">{errors.skillLevel}</p> : null}
        </div>
      </div>

      <div>
        <label className="block primary-text text-[16px]   font-[500] mb-2 text-start mt-4">{t('registration.performance.readiness.label')} <span>*</span></label>
        <div className="space-y-3">
          {['readyNow', 'twoWeeks', 'oneMonthPlus'].map((v) => (
            <Checkbox
              key={v}
              label={t(`registration.performance.readiness.options.${v}`)}
              checked={(formData.readiness || '') === v}
              onChange={() => setSingleChoice('readiness', v)}
            />
          ))}
        {errors.readiness ? <p className="text-red-500 text-[12px] mt-1 text-start ">{errors.readiness}</p> : null}
        </div>
      </div>

      <div>
        <label className="block primary-text text-[16px]   font-[500] mb-2 text-start mt-4">{t('registration.performance.video.label')} </label>
        <div className="mt-2">
          <input
            type="url"
            placeholder={t('registration.performance.video.placeholder')}
            value={formData.videoUrl || ''}
            onChange={setField('videoUrl')}
            onBlur={() => {
              const required = isVideoRequired();
              const val = formData.videoUrl || '';
              const msg = required && !val
                ? t('validation.required')
                : (val && !isValidUrl(val) ? t('validation.invalidUrl') : '');
              setErrors((prev) => ({ ...prev, videoUrl: msg }));
            }}
            className="w-full border-[1px] rounded-[8px] placeholder:text-[#858585] px-[18px] py-2 bg-[#F5F5F5] border-[#E6E6E6] h-[50px] placeholder:text-[16px] text-[16px] focus:bg-[#fff] focus:outline-none focus:ring-[1.5px] focus:ring-[#5D4A9D]"
          />
        {errors.videoUrl ? <p className="text-red-500 text-[12px] mt-1 text-start ">{errors.videoUrl}</p> : null}
        </div>
        <div className="mt-3 space-y-3">
          <Checkbox
            isStart={true}
            checked={(formData.videoAltMethods || []).includes('whatsapp')}
            onChange={() => toggleList('videoAltMethods', 'whatsapp')}
          >
            <>
              {t('registration.performance.videoAlt.whatsappLabel')}
              <a href="https://wa.me/966504336076" target="_blank" rel="noreferrer" className="text-[#5D4A9D] font-[600] inline-block ltrr"> +966 50 433 6076 {" "}</a>
            </>
          </Checkbox>
          <Checkbox
            isStart={true}
            checked={(formData.videoAltMethods || []).includes('email')}
            onChange={() => toggleList('videoAltMethods', 'email')}
          >
            <>
              {t('registration.performance.videoAlt.emailLabel')}
              <a href="mailto:support@hobbystation.co" className="text-[#5D4A9D] font-[600]"> support@hobbystation.co</a>
            </>
          </Checkbox>
        </div>
      </div>

      <div className='pb-4'>
        <label className="block primary-text text-[16px]   font-[500] mb-2 text-start mt-4">{t('registration.performance.financial.label')}</label>
        <div className="space-y-3">
          {['volunteer', 'symbolic', 'professional', 'highProfessional', 'openDiscussion'].map((v) => (
            <Checkbox
              key={v}
              isStart={true}
              label={t(`registration.performance.financial.options.${v}`)}
              checked={(formData.financialExpectation || '') === v}
              onChange={() => setSingleChoice('financialExpectation', v)}
            />
          ))}
        </div>
      </div>
    </div>
  );
}